import { useToast } from "@chakra-ui/react";

import { getRandomChoice, isUndefined, Lang, Logger, safeCall } from "@noli/core";
import { langStore, translate } from "@noli/business";

import type { AIMetaType } from "@/types/meta";
import { Txt2ImgStyles, Txt2ImgArtists, DEFAULT_NEGATIVE_PROMPT_EMBEDDING } from "./constants";
import { loginStore } from "@/modules/login/store";
import { parametersStore } from "@/stores/parameters";
import { styleMapping, artistMapping, catMapping } from "./constants";
import { toast } from "./misc";
import { Editor_Words } from "./lang/editor";

function permuteText(text: string, style?: Txt2ImgStyles, artist?: Txt2ImgArtists): string {
  if (style) {
    const styleText = getRandomChoice(styleMapping[style]);
    text = text ? `${text}, ${styleText}` : styleText;
  }
  if (artist && artist !== "none-artist") {
    const artistText = getRandomChoice(artistMapping[artist]);
    text = text ? `${text}, ${artistText}` : artistText;
  }
  return text;
}

export async function translateAndCheck(
  text: string,
  t: ReturnType<typeof useToast>,
  lang: Lang,
  freezePrompt?: boolean,
  needTranslate?: boolean,
  disableAudit?: boolean,
): Promise<string | undefined> {
  return safeCall(
    async () => {
      if (!text || freezePrompt) return text;
      const res = await fetch(`${parametersStore.creatorHost}/get_prompt`, {
        method: "POST",
        headers: loginStore.jsonPostReqHeaders,
        body: JSON.stringify({
          text,
          need_translate: needTranslate ?? !loginStore.isGuest,
          disable_audit: disableAudit ?? false,
        }),
      });
      if (!res.ok) throw Error(await res.text());
      const jsonRes = await res.json();
      if (jsonRes["success"]) {
        return jsonRes["text"];
      }
      toast(
        t,
        "warning",
        `${translate(Editor_Words["get-prompt-not-safe-warning-message"], lang)}`,
      );
    },
    {
      success: async () => void 0,
      failed: async (e) => {
        Logger.error(`faied to execute 'translateAndCheck' on '${text}' | ${e}`);
        toast(t, "warning", translate(Editor_Words["get-prompt-warning-message"], lang));
        return text;
      },
    },
  );
}

// 返回本次 inject 是否成功
export async function injectNegativePrompt(
  t: ReturnType<typeof useToast>,
  meta: AIMetaType,
): Promise<boolean> {
  const promptInfo = meta.data.promptInfo;
  // 如果没有 promptInfo，说明是旧数据，所以相应的 text 是已经翻译、校验过的了，因此直接返回即可
  if (!promptInfo) {
    return true;
  }
  const { originalNegative } = promptInfo;
  if (!originalNegative) {
    meta.data.negative_prompt = DEFAULT_NEGATIVE_PROMPT_EMBEDDING;
    return true;
  }
  const lang = langStore.tgt;
  const negativePrompt = await translateAndCheck(
    originalNegative,
    t,
    lang,
    false,
    lang === "zh",
    true,
  );
  if (isUndefined(negativePrompt)) {
    return false;
  }
  if (!meta.data.negative_prompt) {
    meta.data.negative_prompt = negativePrompt;
  } else {
    meta.data.negative_prompt = `${meta.data.negative_prompt}, ${negativePrompt}`;
  }

  if (meta.data.custom_embeddings && !negativePrompt.includes(DEFAULT_NEGATIVE_PROMPT_EMBEDDING)) {
    meta.data.negative_prompt = DEFAULT_NEGATIVE_PROMPT_EMBEDDING + negativePrompt;
  }
  return true;
}

// 返回本次 inject 是否成功
export async function injectPrompt(
  t: ReturnType<typeof useToast>,
  meta: AIMetaType,
  freezePrompt?: boolean,
): Promise<boolean> {
  if (freezePrompt) return true;
  const promptInfo = meta.data.promptInfo;
  // 如果没有 promptInfo，说明是旧数据，所以相应的 text 是已经翻译、校验过的了，因此直接返回即可
  if (!promptInfo) {
    return true;
  }
  const { lang, original, style, artist, cat } = promptInfo;
  const translated = await translateAndCheck(original, t, lang);
  if (isUndefined(translated)) {
    return false;
  }
  let final = permuteText(translated, style, artist);
  if (!(await injectNegativePrompt(t, meta))) {
    return false;
  }
  if (cat) {
    const catText = getRandomChoice(catMapping[cat]);
    final = final ? `${catText}, ${final}` : catText;
  }
  if (meta.data.custom_embeddings) {
    const promptEmbeddings = meta.data.custom_embeddings?.filter(
      (item) => item !== DEFAULT_NEGATIVE_PROMPT_EMBEDDING,
    );
    if (promptEmbeddings) {
      final = promptEmbeddings.join("") + final;
    }
  }
  meta.data.text = final;
  return true;
}
